// src/components/ForgotPassword.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { resetPassword, confirmResetPassword, resendSignUpCode } from '@aws-amplify/auth';

function ForgotPassword() {
    const [step, setStep] = useState('request'); // 'request' or 'confirm'
    const [username, setUsername] = useState('');
    const [confirmationCode, setConfirmationCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [showVerificationOption, setShowVerificationOption] = useState(false);
    const navigate = useNavigate();

    const handleRequestReset = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            console.log('Requesting password reset for:', username);
            
            // Call Amplify's resetPassword method
            const resetResult = await resetPassword({
                username
            });
            
            console.log('Reset request result:', resetResult);
            
            setSuccessMessage("Password reset code has been sent to your email.");
            setStep('confirm');
        } catch (err) {
            console.error("Password reset request failed:", err);
            
            // Handle different error types
            if (err.name === 'UserNotFoundException') {
                setError("No account found with this username.");
            } else if (err.name === 'UserNotConfirmedException' || err.message?.includes('not confirmed')) {
                setError("Your account is not verified. Please verify your email first.");
                setShowVerificationOption(true);
            } else if (err.name === 'LimitExceededException') {
                setError("Too many attempts. Please try again later.");
            } else {
                setError(err.message || "Failed to request password reset. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmReset = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        // Validate passwords
        if (newPassword !== confirmNewPassword) {
            setError("Passwords do not match");
            setLoading(false);
            return;
        }

        try {
            console.log('Confirming password reset for:', username);
            
            // Call Amplify's confirmResetPassword method
            const confirmResult = await confirmResetPassword({
                username,
                confirmationCode,
                newPassword
            });
            
            console.log('Confirm reset result:', confirmResult);
            
            setSuccessMessage("Password has been reset successfully! You can now log in with your new password.");
            
            // Clear form
            setNewPassword('');
            setConfirmNewPassword('');
            setConfirmationCode('');
        } catch (err) {
            console.error("Password reset confirmation failed:", err);
            
            // Handle different error types
            if (err.name === 'CodeMismatchException') {
                setError("Verification code is incorrect. Please try again.");
            } else if (err.name === 'ExpiredCodeException') {
                setError("Verification code has expired. Please request a new one.");
            } else if (err.name === 'InvalidPasswordException') {
                setError("Password does not meet requirements: " + err.message);
            } else {
                setError(err.message || "Failed to reset password. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const restartProcess = () => {
        setStep('request');
        setUsername('');
        setConfirmationCode('');
        setNewPassword('');
        setConfirmNewPassword('');
        setError(null);
        setSuccessMessage(null);
        setShowVerificationOption(false);
    };

    const handleResendVerificationCode = async () => {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            console.log('Resending verification code for:', username);
            
            await resendSignUpCode({ username });
            
            setSuccessMessage("Verification code has been sent to your email. Please check your inbox.");
            setShowVerificationOption(false);
            
            // Redirect to signup verification page after 3 seconds
            setTimeout(() => {
                navigate(`/signup?verify=true&username=${encodeURIComponent(username)}`);
            }, 3000);
        } catch (err) {
            console.error("Failed to resend verification code:", err);
            
            if (err.name === 'UserNotFoundException') {
                setError("No account found with this username.");
            } else if (err.name === 'LimitExceededException') {
                setError("Too many attempts. Please try again later.");
            } else {
                setError(err.message || "Failed to resend verification code. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto my-12">
            <div className="bg-white p-8 shadow-lg rounded-lg">
                <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
                    {step === 'request' ? 'Reset Your Password' : 'Enter New Password'}
                </h2>
                
                {error && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                        {error}
                    </div>
                )}
                
                {successMessage && (
                    <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
                        {successMessage}
                    </div>
                )}
                
                {step === 'request' ? (
                    <form onSubmit={handleRequestReset}>
                        <div className="mb-6">
                            <label className="block text-gray-700 mb-2" htmlFor="username">
                                Username or Email
                            </label>
                            <input
                                id="username"
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className="border border-gray-300 p-3 w-full rounded"
                                required
                            />
                        </div>
                        
                        <button
                            type="submit"
                            className={`w-full py-3 px-4 rounded text-white font-medium ${
                                loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                            disabled={loading}
                        >
                            {loading ? "Sending Reset Code..." : "Send Reset Code"}
                        </button>
                        
                        {showVerificationOption && username && (
                            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <p className="text-sm text-yellow-800 mb-3">
                                    Your account needs to be verified before you can reset your password.
                                </p>
                                <button
                                    type="button"
                                    onClick={handleResendVerificationCode}
                                    className={`w-full py-2 px-4 rounded text-white font-medium ${
                                        loading ? 'bg-gray-400' : 'bg-yellow-600 hover:bg-yellow-700'
                                    }`}
                                    disabled={loading}
                                >
                                    {loading ? "Sending Verification Code..." : "Send Verification Code"}
                                </button>
                            </div>
                        )}
                    </form>
                ) : (
                    <form onSubmit={handleConfirmReset}>
                        <div className="mb-4">
                            <label className="block text-gray-700 mb-2" htmlFor="confirmationCode">
                                Verification Code
                            </label>
                            <input
                                id="confirmationCode"
                                type="text"
                                value={confirmationCode}
                                onChange={(e) => setConfirmationCode(e.target.value)}
                                className="border border-gray-300 p-3 w-full rounded"
                                required
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Please enter the verification code sent to your email.
                            </p>
                        </div>
                        
                        <div className="mb-4">
                            <label className="block text-gray-700 mb-2" htmlFor="newPassword">
                                New Password
                            </label>
                            <input
                                id="newPassword"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="border border-gray-300 p-3 w-full rounded"
                                required
                                minLength="8"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Password must be at least 8 characters and include uppercase, lowercase, numbers, and special characters.
                            </p>
                        </div>
                        
                        <div className="mb-6">
                            <label className="block text-gray-700 mb-2" htmlFor="confirmNewPassword">
                                Confirm New Password
                            </label>
                            <input
                                id="confirmNewPassword"
                                type="password"
                                value={confirmNewPassword}
                                onChange={(e) => setConfirmNewPassword(e.target.value)}
                                className="border border-gray-300 p-3 w-full rounded"
                                required
                            />
                        </div>
                        
                        <button
                            type="submit"
                            className={`w-full py-3 px-4 rounded text-white font-medium ${
                                loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                            disabled={loading}
                        >
                            {loading ? "Resetting Password..." : "Reset Password"}
                        </button>
                        
                        <div className="mt-4 text-center">
                            <button
                                type="button"
                                onClick={restartProcess}
                                className="text-blue-600 hover:underline text-sm"
                            >
                                Start over
                            </button>
                        </div>
                    </form>
                )}
                
                <div className="mt-6 text-center space-y-2">
                    <p className="text-gray-600">
                        Remember your password?{' '}
                        <Link to="/login" className="text-blue-600 hover:underline">
                            Log in
                        </Link>
                    </p>
                    <p className="text-gray-600">
                        Need to verify your account?{' '}
                        <Link to="/signup?verify=true" className="text-blue-600 hover:underline">
                            Verify Email
                        </Link>
                    </p>
                    <p className="text-gray-600">
                        Don't have an account?{' '}
                        <Link to="/signup" className="text-blue-600 hover:underline">
                            Sign up
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default ForgotPassword;